import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { fetchJobseekersData } from 'features/jobseekers/jobseekersThunks';

import { Jobseeker, JobseekersList, JobseekersState } from 'types/jobseekers';

import { RequestStatus } from 'constants/enums';

const initialState: JobseekersState = {
  jobseekers: [],
  facets: {
    country: {},
    city: {},
    stage: {},
    citizenship: {},
    education: {},
    languages: {},
    skillsets: {}
  },
  status: RequestStatus.Idle,
  error: null,
  count: 0,
  listMeta: null
};

const jobseekersSlice = createSlice({
  name: 'jobseekers',
  initialState,
  reducers: {
    addJobseeker: (state, action: PayloadAction<Jobseeker>) => {
      state.jobseekers = [...state.jobseekers, action.payload];
    },
    updateJobseeker: (state, action: PayloadAction<Partial<Jobseeker>>) => {
      const { id, ...changes } = action.payload;
      state.jobseekers = state.jobseekers.map((jobseeker) =>
        jobseeker.id === id ? { ...jobseeker, ...changes } : jobseeker
      );
    },
    dropJobseekers: (state) => {
      state.jobseekers = [];
      state.count = 0;
      state.listMeta = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchJobseekersData.pending, (state) => {
        state.status = RequestStatus.Loading;
      })
      .addCase(
        fetchJobseekersData.fulfilled,
        (state, action: PayloadAction<JobseekersList>) => {
          state.status = RequestStatus.Succeeded;
          state.jobseekers = action.payload.content;
          if (action.payload.facets) {
            state.facets = action.payload.facets;
          }
          state.count = action.payload.totalElements;
          state.listMeta = {
            empty: action.payload.empty,
            first: action.payload.first,
            last: action.payload.last,
            pageable: action.payload.pageable,
            totalElements: action.payload.totalElements,
            totalPages: action.payload.totalPages,
            number: action.payload.number,
            numberOfElements: action.payload.numberOfElements,
            size: action.payload.size
          };
          state.error = null;
        }
      )
      .addCase(fetchJobseekersData.rejected, (state, action) => {
        state.status = RequestStatus.Failed;
        state.error = action.error.message || null;
      });
  }
});

export const { addJobseeker, updateJobseeker, dropJobseekers } =
  jobseekersSlice.actions;

export default jobseekersSlice.reducer;
